import { useState, useEffect } from "react";
import { Sidebar, Menu, MenuItem } from 'react-pro-sidebar';
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { tokens } from "../../theme";
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import PieChartOutlineOutlinedIcon from '@mui/icons-material/PieChartOutlineOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';

import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import AirlineSeatIndividualSuiteOutlinedIcon from '@mui/icons-material/AirlineSeatIndividualSuiteOutlined';
import CellTowerOutlinedIcon from '@mui/icons-material/CellTowerOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import HomeRepairServiceOutlinedIcon from '@mui/icons-material/HomeRepairServiceOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import DeveloperBoardOutlinedIcon from '@mui/icons-material/DeveloperBoardOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';

import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EscalatorWarningOutlinedIcon from '@mui/icons-material/EscalatorWarningOutlined';
import FamilyRestroomOutlinedIcon from '@mui/icons-material/FamilyRestroomOutlined';
import BlindOutlinedIcon from '@mui/icons-material/BlindOutlined';
import HandymanIcon from '@mui/icons-material/Handyman';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CashbookView from "../admin/CashbookView";

const Item = ({ title, to, icon, selected, setSelected }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [isHovered, setIsHovered] = useState(false);
    const location = useLocation();
    
    // Check if this item should be active based on current path
    const isActive = location.pathname === to || selected === title;

    return (
        <MenuItem
            active={isActive}
            style={{
                color: isActive ? colors.blue[400] : colors.white[700],
                backgroundColor: isActive 
                    ? colors.white[500] 
                    : isHovered 
                        ? colors.white[500] 
                        : 'transparent',
                transition: 'background-color 0.1s ease',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => setSelected(title)}
            icon={icon}
            component={<Link to={to} />}
        >
            <Typography>{title}</Typography>
        </MenuItem>
    );
};

const SideBar = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [selected, setSelected] = useState("Dashboard");
    const location = useLocation();

    // Update selected state based on current path
    useEffect(() => {
        const path = location.pathname;
        
        if (path === "/admin/dashboard") setSelected("Dashboard");
        else if (path === "/admin/dashboard/leave") setSelected("Leave");
        else if (path === "/admin/dashboard/borrow") setSelected("Borrow");
        else if (path === "/admin/dashboard/nextofkin") setSelected("Next of Kin");
        else if (path === "/admin/dashboard/parentsguardians") setSelected("Parents/Guardians");
        else if (path === "/admin/dashboard/students") setSelected("Students");
        else if (path === "/admin/dashboard/staffs") setSelected("Staffs");
        else if (path === "/admin/dashboard/inventory") setSelected("Inventory");
        else if (path === "/admin/dashboard/calender") setSelected("Calender");
        else if (path === "/admin/dashboard/contact/event") setSelected("Event");
        else if (path === "/admin/dashboard/contact/news") setSelected("News");
        else if (path === "/admin/dashboard/contact/noticeboard") setSelected("Notice Board");
        else if (path === "/admin/dashboard/contact/carear") setSelected("Vacance");
        else if (path === "/admin/dashboard/contact/faq") setSelected("FAQ Page");
        else if (path === "/admin/dashboard/contact/barchart") setSelected("Bar Chart");
        else if (path === "/admin/dashboard/contact/piechart") setSelected("Pie Chart");
        else if (path === "/admin/dashboard/contact/linechart") setSelected("line Chart");
        else if (path === "/admin/dashboard/contact/geographychart") setSelected("Geography Chart");
        else if (path === "/admin/dashboard/distribution") setSelected("Distribution");
        else if (path === "/admin/dashboard/cashbook") setSelected("Cashbook");
        // Add more mappings as needed
    }, [location.pathname]);

    return (
        <Box>
            <Sidebar 
                collapsed={isCollapsed}
                backgroundColor={colors.dark[800]}
                rootStyles={{
                    background: colors.dark[900],
                    height: "100vh",
                    padding: "0",
                    borderRight: "none"
                }}
            >
                <Menu iconShape="square">
                    <MenuItem
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
                        style={{
                            margin: "10px 0 20px 0",
                            color: colors.dark[400],
                            background: colors.dark[800]
                        }}
                    >
                        {!isCollapsed && (
                            <Box 
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                ml="15px"
                            >
                                <Typography variant="h3" color={colors.primary[100]}>ADMIN</Typography>
                                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                                    <MenuOutlinedIcon />
                                </IconButton>
                            </Box>
                        )}
                    </MenuItem>
                    
                    <Box paddingLeft={isCollapsed ? undefined : "10%"}>
                        <Item
                            title="Dashboard"
                            to="/admin/dashboard"
                            icon={<HomeOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                    </Box>
                    
                    <Typography
                        variant="h6"
                        color={colors.blue[400]}
                        sx={{m:"15px 0 5px 20px"}}
                    >
                        Manage
                    </Typography>
                    
                    <Box paddingLeft={isCollapsed ? undefined : "10%"}>
                        <Item
                            title="Send Emails"
                            to="https://webmailweb-cpt.konsoleh.co.za/?_task=mail&_mbox=INBOX"
                            icon={<ForwardToInboxOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Leave"
                            to="/admin/dashboard/leave"
                            icon={<AirlineSeatIndividualSuiteOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Borrow"
                            to="/admin/dashboard/borrow"
                            icon={<LaptopMacOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Next of Kin"
                            to="/admin/dashboard/nextofkin"
                            icon={<BlindOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Parents/Guardians"
                            to="/admin/dashboard/parentsguardians"
                            icon={<FamilyRestroomOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Students"
                            to="/admin/dashboard/students"
                            icon={<EscalatorWarningOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Staffs"
                            to="/admin/dashboard/staffs"
                            icon={<PeopleOutlineOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                    </Box>
                    <Typography
                        variant="h6"
                        color={colors.blue[400]}
                        sx={{m:"15px 0 5px 20px"}}
                    >
                        Accounting
                    </Typography>
                    <Box paddingLeft={isCollapsed ? undefined : "10%"}>
                        {/* Cashbook link */}
                        <Item
                            title="Cashbook"
                            to="/admin/dashboard/cashbook"
                            icon={<AccountBalanceOutlinedIcon/>}
                            selected={selected}
                        />

                        <Item
                            title="Assets Register"
                            to="/admin/dashboard/inventory"
                            icon={<HandymanIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                        <Item
                            title="Consumable Register"
                            to="/admin/dashboard/distribution"
                            icon={<Inventory2OutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                    </Box>
                    
                    <Typography
                        variant="h6"
                        color={colors.blue[400]}
                        sx={{m:"15px 0 5px 20px"}}
                    >
                        Pages
                    </Typography>
                    
                    <Box paddingLeft={isCollapsed ? undefined : "10%"}>
                        <Item
                            title="Calender"
                            to="/admin/dashboard/calender"
                            icon={<CalendarTodayOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
            
                        <Item
                            title="Vacance"
                            to="/admin/dashboard/contact/carear"
                            icon={<WorkOutlineOutlinedIcon/>}
                            selected={selected}
                            setSelected={setSelected}
                        />
                    </Box>
                </Menu>
            </Sidebar>
        </Box>
    );
}

export default SideBar;